import { z } from 'zod'

/**
 * Spec 40 - bijtelling (splittarief EV + auto als bron). Gedeeld tussen API
 * (validatie + read-DTO) en backoffice (form-validatie + tabel-rendering). Eén
 * bron, getoond op het tarieventabel-beheerscherm (SALARIS/MEDEWERKERS) én als
 * afgeleide regeling op het voertuig/contract.
 *
 * De pure rekenlogica leeft in `@streep/utils/bijtelling` (`berekenBijtelling`).
 * Hier staan alleen het beheer-schema voor de `BijtellingTarief`-tabel en de
 * read-DTO's. Percentages reizen als heel-procent (16, 22), bedragen als getal
 * in euro (Decimal in de DB, number in de DTO). Datums als ISO-string.
 */

// ─── Brandstofcategorie (gelijk aan de Prisma-enum) ─────────────────────────

export const BIJTELLING_BRANDSTOF_CATEGORIEEN = ['NULEMISSIE', 'OVERIG'] as const
export type BijtellingBrandstofCategorie = (typeof BIJTELLING_BRANDSTOF_CATEGORIEEN)[number]

export const BIJTELLING_BRANDSTOF_CATEGORIE_LABELS: Record<BijtellingBrandstofCategorie, string> = {
  NULEMISSIE: 'Nulemissie (elektrisch/waterstof)',
  OVERIG: 'Overig (algemeen tarief)',
}

// ─── Beheer-schema (create + update body) ───────────────────────────────────
//
// Gedeeld door de API (`.safeParse` op POST/PATCH) en het beheerform. Eén bron:
// het form kán niets sturen dat de API weigert.

export const bijtellingTariefSchema = z.object({
  /** Jaar van datum eerste toelating waarvoor dit regime geldt (bv. 2023). */
  regimeJaar: z.number().int().min(2000).max(2100),
  brandstofCategorie: z.enum(['NULEMISSIE', 'OVERIG']),
  /** Verlaagd tarief, heel-procent (bv. 16). */
  laagPercentage: z.number().min(0).max(100),
  /** Cataloguswaarde-grens in euro; null = geen cap (heel bedrag tegen laag%). */
  capBedrag: z.number().min(0).nullable(),
  /** Tarief boven de cap / algemeen tarief, heel-procent (bv. 22). */
  hoogPercentage: z.number().min(0).max(100),
  /** 60-maanden-klik (default 60). */
  looptijdMaanden: z.number().int().positive().default(60),
})
export type BijtellingTariefInput = z.infer<typeof bijtellingTariefSchema>

// ─── Read-DTO's ──────────────────────────────────────────────────────────────

/** Eén tariefregel zoals de API hem teruggeeft (Decimals -> number, datums ISO). */
export interface BijtellingTariefDTO {
  id: string
  regimeJaar: number
  brandstofCategorie: BijtellingBrandstofCategorie
  laagPercentage: number
  capBedrag: number | null
  hoogPercentage: number
  looptijdMaanden: number
  createdAt: string
  updatedAt: string
}

export type BijtellingRegelingStatus = 'BEREKEND' | 'INPUT_ONVOLLEDIG'

/**
 * De berekende regeling voor weergave (afgeleid van de auto). Spiegelt
 * `BijtellingResultaat` uit `@streep/utils`, maar als serialiseerbare DTO:
 * `vervaltOp` als ISO-string i.p.v. Date, zonder de interne `regelingId`/`reden`.
 */
export interface BijtellingRegelingDTO {
  status: BijtellingRegelingStatus
  jaarbedrag: number | null
  maandbedrag: number | null
  /** Effectief bijtellingspercentage = jaarbedrag / cataloguswaarde x 100. */
  effectiefPercentage: number | null
  laagPercentage: number | null
  capBedrag: number | null
  hoogPercentage: number | null
  regimeJaar: number | null
  /** 1e dag van de maand na DET + looptijd (60-mnd-klik); null als n.v.t. */
  vervaltOp: string | null
  brandstofCategorie: BijtellingBrandstofCategorie
}
